import Vue from 'vue'

/**
 * The registry uses a singleton model to persist all namespaced plugins.
 */
export const registry = Vue.observable({})

/**
 * Register a new plugin at the specified namespace.
 * @param {String} namespace - A unique identifier to organize plugins.
 * @param {Function|Object} plugin
 * @return {Function} - Call to unregister the plugin.
 */
export function register (namespace, plugin) {
  if (!registry[namespace]) {
    Vue.set(registry, namespace, [])
  }

  if (!plugin) return
  if (registry[namespace].indexOf(plugin) < 0) {
    registry[namespace].push(plugin)
  }
  return () => unregister(namespace, plugin)
}

/**
 * Remove one or more plugins from the registry if currently installed.
 * @param {String} namespace - The namespace to which the plugin belongs.
 * @param {Function|Object} plugin - If not provided the namespace is unregistered.
 */
export const unregister = (namespace, plugin) => {
  if (!plugin) {
    Vue.delete(registry, namespace)
    return
  }
  const atIndex = registry[namespace].indexOf(plugin)
  if (atIndex < 0) return
  registry[namespace].splice(atIndex, 1)
}

/**
 * Generate a mixin for dependency injection of plugins at the specified namespace.
 * When this is used on a parent component, children can get a reactive list of
 * plugins by adding `inject: ['plugins']` to the component.
 * @param {String} withNamespace - The plugin namespace to inject.
 * @see https://vuejs.org/v2/api/#provide-inject
 */
export default withNamespace => {
  register(withNamespace)

  return {
    provide: {
      plugins: registry[withNamespace]
    }
  }
}
